import { apiPath } from "../../lib/apiPath";

const jsonHeaders = { "Content-Type": "application/json", Accept: "application/json" };

export type TaskSection = "daily" | "weekly" | "permanent";
export type TaskRewardKind = "dots" | "exclusive_item" | "shop_item" | "free_crate";
export type TaskMetric = "score" | "time";

export type TaskRow = {
  id: string;
  section: TaskSection;
  modeId: string;
  modeLabel: string;
  metric: TaskMetric;
  goalValue: number;
  levelIndex: number | null;
  rewardKind: TaskRewardKind;
  rewardDots: number;
  rewardCosmeticId: string | null;
  rewardCrateId: string | null;
  description: string;
  sortOrder: number;
  periodKey: string;
  periodEndsAt: number | null;
  progressValue: number;
  completed: boolean;
  claimed: boolean;
  claimable: boolean;
  rewardLabel: string;
  rewardName: string | null;
};

export type TasksListResponse = {
  daily: TaskRow[];
  weekly: TaskRow[];
  permanent: TaskRow[];
  claimableCount: number;
  timeZone: string;
  dailyPeriodKey?: string;
  weeklyPeriodKey?: string;
  dailyEndsAt?: number | null;
  weeklyEndsAt?: number | null;
  freeCrateRolls?: Record<string, number>;
  message?: string;
};

export type TaskClaimResult = {
  ok: boolean;
  rewardKind: TaskRewardKind;
  rewardLabel: string;
  rewardDots?: number;
  dots?: { balance?: number };
  rewardCosmetic: {
    id: string;
    name: string;
    imageData: string;
    animationFrames: string[] | null;
    newCursor: boolean;
    category: string;
  } | null;
  rewardCrateId: string | null;
  rewardCrateName: string | null;
  freeCrateRollsLeft: number;
  claimableCount: number;
  freeCrateRolls: Record<string, number>;
  battlePass?: {
    granted: number;
    levelsGained: number;
    level: number;
    xp: number;
    xpToNext: number;
  };
};

function clientTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export async function fetchTasks(): Promise<TasksListResponse> {
  const tz = encodeURIComponent(clientTimeZone());
  const r = await fetch(`${apiPath("/api/tasks")}?timeZone=${tz}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 401) {
    return {
      daily: [],
      weekly: [],
      permanent: [],
      claimableCount: 0,
      timeZone: clientTimeZone(),
    };
  }
  if (!r.ok) throw new Error(`tasks ${r.status}`);
  return r.json();
}

export async function reportTaskProgress(input: {
  modeId: string;
  score: number;
  timeSec: number;
  levelIndex?: number | null;
}): Promise<{ updated: number; newlyCompleted: number; claimableCount: number }> {
  const r = await fetch(apiPath("/api/tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      action: "report-progress",
      modeId: input.modeId,
      score: input.score,
      timeSec: input.timeSec,
      levelIndex: input.levelIndex ?? undefined,
    }),
  });
  if (r.status === 401) return { updated: 0, newlyCompleted: 0, claimableCount: 0 };
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `tasks progress ${r.status}`);
  return {
    updated: Number(data.updated) || 0,
    newlyCompleted: Number(data.newlyCompleted) || 0,
    claimableCount: Number(data.claimableCount) || 0,
  };
}

export async function claimTask(taskId: string): Promise<TaskClaimResult> {
  const r = await fetch(apiPath("/api/tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ action: "claim", taskId }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `claim ${r.status}`);
  return data as TaskClaimResult;
}

export type AdminTaskRow = {
  id: string;
  section: TaskSection;
  description: string | null;
  displayDescription: string;
  modeId: string;
  metric: TaskMetric;
  goalValue: number;
  levelIndex: number | null;
  rewardKind: TaskRewardKind;
  rewardDots: number;
  rewardCosmeticId: string | null;
  rewardCrateId: string | null;
  rewardName: string | null;
  rewardLabel?: string;
  probabilityBasisPoints?: number;
  probabilityPercent?: number;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export async function fetchAdminTasks(): Promise<AdminTaskRow[]> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error(`admin-tasks ${r.status}`);
  const data = await r.json();
  return Array.isArray(data.tasks) ? data.tasks : [];
}

export async function fetchAdminTaskShopItems(): Promise<
  Array<{ id: string; name: string; category: string }>
> {
  const r = await fetch(`${apiPath("/api/admin-tasks")}?section=shop-items`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`shop-items ${r.status}`);
  const data = await r.json();
  return Array.isArray(data.items) ? data.items : [];
}

export type AdminTaskCrateOption = {
  id: string;
  name: string;
  isPublished?: boolean;
};

export async function fetchAdminTaskCrates(): Promise<AdminTaskCrateOption[]> {
  const r = await fetch(`${apiPath("/api/admin-tasks")}?section=crates`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`crates ${r.status}`);
  const data = await r.json();
  return Array.isArray(data.crates) ? data.crates : [];
}

export async function fetchAdminTaskCosmetics(): Promise<
  Array<{ id: string; name: string; category: string; taskOnly?: boolean }>
> {
  const r = await fetch(`${apiPath("/api/admin-tasks")}?section=cosmetics`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`cosmetics ${r.status}`);
  const data = await r.json();
  return Array.isArray(data.cosmetics) ? data.cosmetics : [];
}

function apiErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const err = (data as { error?: unknown }).error;
  if (typeof err === "string" && err.trim()) return err;
  if (err && typeof err === "object") {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
    try {
      return JSON.stringify(err);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export async function createAdminTask(body: Record<string, unknown>): Promise<AdminTaskRow> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(data, `create ${r.status}`));
  if (!data || typeof data !== "object" || !(data as { task?: unknown }).task) {
    throw new Error("create_failed");
  }
  return (data as { task: AdminTaskRow }).task;
}

export async function updateAdminTask(body: Record<string, unknown>): Promise<AdminTaskRow> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "PATCH",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(data, `update ${r.status}`));
  if (!data || typeof data !== "object" || !(data as { task?: unknown }).task) {
    throw new Error("update_failed");
  }
  return (data as { task: AdminTaskRow }).task;
}

export async function deleteAdminTask(id: string): Promise<void> {
  const r = await fetch(`${apiPath("/api/admin-tasks")}?${new URLSearchParams({ id })}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`delete ${r.status}`);
}

export async function saveAdminTaskProbabilities(
  taskSection: "daily" | "weekly",
  weights: Array<{ id: string; probabilityPercent: number }>,
): Promise<AdminTaskRow[]> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "probabilities", taskSection, weights }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(data, `probabilities ${r.status}`));
  return Array.isArray((data as { tasks?: unknown }).tasks)
    ? ((data as { tasks: AdminTaskRow[] }).tasks)
    : [];
}

export async function equalizeAdminTaskProbabilities(
  taskSection: "daily" | "weekly",
): Promise<AdminTaskRow[]> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "equalize", taskSection }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(data, `equalize ${r.status}`));
  return Array.isArray((data as { tasks?: unknown }).tasks)
    ? ((data as { tasks: AdminTaskRow[] }).tasks)
    : [];
}

export type AdminTaskResetResult = {
  ok: boolean;
  section: TaskSection;
  periodKey: string;
  progressDeleted: number;
  resetTaskCount: number;
  excludedTaskCount: number;
  reshuffled: boolean;
  reshuffledTaskIds: string[] | null;
};

export async function resetAdminTasks(input: {
  taskSection: TaskSection;
  excludeTaskIds: string[];
  reshuffle: boolean;
}): Promise<AdminTaskResetResult> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({
      section: "reset",
      taskSection: input.taskSection,
      excludeTaskIds: input.excludeTaskIds,
      reshuffle: input.reshuffle,
    }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(apiErrorMessage(data, `reset ${r.status}`));
  return data as AdminTaskResetResult;
}

export async function createAdminTaskCosmetic(body: Record<string, unknown>): Promise<{
  id: string;
  name: string;
  category: string;
}> {
  const r = await fetch(apiPath("/api/admin-tasks"), {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders,
    body: JSON.stringify({ section: "cosmetic", ...body }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `cosmetic ${r.status}`);
  return data.cosmetic;
}
